/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { RefObject, ComponentChildren } from 'preact';
import { useRef } from 'preact/hooks';
import { DataState, ItemContext, Range, Metadata } from '../UNSAFE_Collection';
import { Collection } from '../PRIVATE_Collection';
import { useViewportIntersect, ViewportConfig } from '../hooks/UNSAFE_useViewportIntersect';

/**
 * Props for the VirtualizedCollection component
 */
type Props<K, D> = {
  /**
   * A DataState object that provide information including data and metadata to the VirtualizedCollection.
   */
  data: DataState<K, D> | null;

  /**
   * Renderer callback used to render each item
   */
  children: (context: ItemContext<{ data: D; metadata: Metadata<K> }>) => ComponentChildren;

  /**
   * Viewport configuration including specifying the element which is used to determine what range
   * of data to render
   */
  viewportConfig: ViewportConfig;

  /**
   * Callback function to handle when viewport has changed, including the case
   * where user scrolls to the end of the component and there are more items to load.
   * The function should sets a new DataState on the component for the specified range.
   */
  onLoadRange: (range: Range) => void;

  /**
   * Optionally specify the custom load more indicator
   */
  loadMoreIndicator?: ComponentChildren;

  /**
   * Optionally provide the suggestion indicator sparkle
   */
  suggestions?: ComponentChildren;
};

const isDataStateValid = <K, D>(dataState: DataState<K, D>) => {
  return dataState && dataState.data && dataState.offset >= 0 && dataState.totalSize >= 0;
};

const DEFAULT_MARGIN = 1;
const DEFAULT_FETCH_SIZE = 25;

const LOADMORE_STYLE_CLASS = 'oj-collection-loadmore';
const LOAD_MORE_SELECTOR = `.${LOADMORE_STYLE_CLASS}`;

/**
 * Component that only render items in the specified viewport.
 * @param props
 */
export function LoadMoreCollection<K, D>({
  data,
  children,
  viewportConfig,
  onLoadRange,
  loadMoreIndicator,
  suggestions
}: Props<K, D>) {
  const rootRef: RefObject<any> = useRef<HTMLElement>(null);
  const fetchSizeRef: RefObject<number> = useRef<number>(-1);

  // detect if viewport has changed and it needs to re-render based on a new range
  useViewportIntersect(viewportConfig, DEFAULT_MARGIN, LOAD_MORE_SELECTOR, () => {
    if (data) {
      if (fetchSizeRef.current == null || fetchSizeRef.current <= 0) {
        fetchSizeRef.current = data.totalSize;
      }
      const fetchSize =
        fetchSizeRef.current && fetchSizeRef.current > 0
          ? fetchSizeRef.current
          : DEFAULT_FETCH_SIZE;
      onLoadRange({
        offset: 0,
        count: data.totalSize + fetchSize
      });
    }
  });

  // override itemRenderer to adjust the index
  const _getItemRenderer = (startIndex: number) => {
    // returns a new render function
    return (context: ItemContext<{ data: D; metadata: Metadata<K> }>) => {
      const itemContext: ItemContext<{ data: D; metadata: Metadata<K> }> = {
        index: startIndex + context.index,
        data: context.data
      };
      return children(itemContext);
    };
  };

  // if DataState is invalid (including not specified), just render blank
  if (!data || !isDataStateValid(data)) {
    return <div ref={rootRef} />;
  }

  const dataState = data;
  const rangeData = dataState.data;
  const offset = dataState.offset;
  const endIndex = offset + rangeData.length;
  const itemCount = dataState.totalSize;

  const renderLoadMore =
    loadMoreIndicator && dataState.sizePrecision === 'atLeast' && endIndex <= itemCount;

  // might need to support elementType prop in the future to allow customization of what
  // type of element to render for the root
  return (
    <div ref={rootRef}>
      {suggestions}
      <Collection items={rangeData}>{_getItemRenderer(offset)}</Collection>
      {renderLoadMore && <div class={LOADMORE_STYLE_CLASS}>{loadMoreIndicator}</div>}
    </div>
  );
}
